import { ArrowRight, CheckCircle2, FileText, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { discordLoginUrl } from "@/lib/api";

const publicHighlights = [
  {
    title: "Formularz rekrutacyjny",
    text: "Wypełnij zgłoszenie na Junior Dev Operative i prześlij je do weryfikacji.",
  },
  {
    title: "Status zgłoszenia",
    text: "Wracaj do panelu, aby śledzić aktualny etap i kolejne kroki.",
  },
  {
    title: "Poufny proces",
    text: "Całość została przygotowana jako uporządkowany i bezpieczny proces aplikacyjny.",
  },
];

export default function LoginPage() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="public-home-hero">
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-primary/80">Byteflow</p>
            <h1 className="text-lg font-semibold">Junior Dev Operative</h1>
          </div>
          <div className="hidden items-center gap-3 md:flex">
            <a href="#form-section" data-testid="public-nav-form-link">
              <Button variant="ghost">Formularz rekrutacyjny</Button>
            </a>
            <a href="#status-section" data-testid="public-nav-status-link">
              <Button variant="ghost">Status zgłoszenia</Button>
            </a>
            <a href={discordLoginUrl} data-testid="public-nav-primary-cta">
              <Button>Aplikuj</Button>
            </a>
          </div>
        </div>
      </header>

      <section className="login-panel-texture relative overflow-hidden border-b border-border px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="grid-overlay pointer-events-none absolute inset-0" />
        <div className="relative z-10 mx-auto grid w-full max-w-6xl gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div className="space-y-6">
            <Badge className="border-primary/30 bg-primary/12 px-3 py-1 text-primary">Byteflow</Badge>
            <div className="space-y-4">
              <h2 className="max-w-3xl text-4xl font-[650] tracking-tight sm:text-5xl lg:text-6xl">
                Junior Dev Operative
              </h2>
              <p className="max-w-2xl text-base leading-relaxed text-slate-200/78 sm:text-lg">
                Złóż zgłoszenie w kilka minut. W jednym miejscu uzupełnisz formularz, sprawdzisz status i poznasz kolejne etapy Byteflow.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a href={discordLoginUrl} data-testid="hero-primary-cta">
                <Button className="h-12 px-6 text-sm font-medium">
                  Rozpocznij zgłoszenie <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
              <a href="#status-section" data-testid="hero-secondary-cta">
                <Button variant="secondary" className="h-12 px-6 text-sm font-medium">
                  Sprawdź status
                </Button>
              </a>
            </div>
            <p className="text-sm text-slate-200/68">
              Twoje dane są przetwarzane zgodnie z zasadami bezpieczeństwa i poufności.
            </p>
          </div>

          <Card id="status-section" className="surface-noise border-white/10 bg-black/30 shadow-[0_18px_60px_rgba(0,0,0,0.45)]">
            <CardContent className="space-y-6 p-6 sm:p-8">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-primary/80">Status zgłoszenia</p>
                  <h3 className="mt-2 text-2xl font-semibold tracking-tight">Przejrzysty proces</h3>
                </div>
                <div className="rounded-2xl border border-primary/20 bg-primary/10 p-3 text-primary">
                  <ShieldCheck className="h-5 w-5" />
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-2xl border border-border bg-background/75 p-4">
                  <div className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground">
                    <CheckCircle2 className="h-4 w-4 text-primary" /> 1. Wypełnij formularz
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    Uzupełnij wszystkie wymagane informacje, aby przejść do kolejnego etapu.
                  </p>
                </div>
                <div className="rounded-2xl border border-border bg-background/75 p-4">
                  <div className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground">
                    <FileText className="h-4 w-4 text-primary" /> 2. Oczekuj na weryfikację
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    Po wysłaniu zgłoszenia jego status będzie dostępny w Twoim panelu.
                  </p>
                </div>
                <div className="rounded-2xl border border-border bg-background/75 p-4">
                  <div className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground">
                    <ShieldCheck className="h-4 w-4 text-primary" /> 3. Poznaj wynik
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    Gdy weryfikacja zostanie zakończona, zobaczysz aktualizację i dalsze instrukcje.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section id="form-section" className="px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
        <div className="mx-auto w-full max-w-6xl space-y-6">
          <div className="max-w-2xl space-y-3">
            <p className="text-xs uppercase tracking-[0.24em] text-primary/80">Proces aplikacyjny</p>
            <h3 className="text-3xl font-semibold tracking-tight">Wszystko przygotowane dla kandydatów Dev</h3>
            <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
              Portal został zaprojektowany tak, aby przejść przez rekrutację na Junior Dev Operative w spokojny, uporządkowany i nowoczesny sposób.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {publicHighlights.map((item) => (
              <Card key={item.title} className="surface-noise card-hover border-border bg-card/75">
                <CardContent className="p-6">
                  <p className="text-sm font-semibold text-foreground">{item.title}</p>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
